// frontend/src/utils/portal.js
// 🔒 ISOLATION PAR DOMAINE — chaque rôle ne peut se connecter que sur SON portail.
//   admin.gowele.com   → Super Admin uniquement
//   company.gowele.com → Admin compagnie + staff compagnie uniquement
//   driver.gowele.com  → Chauffeur uniquement
//   gowele.com         → Client uniquement
// Le portail est déduit du hostname (jamais d'un choix utilisateur, jamais d'un rôle
// « passe-partout »). Un compte dont le rôle ne correspond pas au portail est REFUSÉ
// à la connexion (aucune session stockée, aucune redirection automatique).

export const PORTALS = { SUPER: 'super', COMPANY: 'company', DRIVER: 'driver', PUBLIC: 'public', DEV: 'dev' };

// Staff RBAC autorisé sur l'espace compagnie (miroir de COMPANY_ASSIGNABLE côté backend).
const STAFF_ROLES = ['CHEF_AGENCE', 'RESERVATION_AGENT', 'TRAFFIC_AGENT', 'FINANCE_AGENT', 'SUPPORT_AGENT', 'OPERATOR'];

// Portail courant d'après le hostname. Les hôtes non canoniques (localhost, *.vercel.app,
// gowele-admin.onrender.com de preview) → DEV : aucun blocage (pour ne pas casser dev/preview).
export const getPortal = (hostname) => {
  const h = (hostname || (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase();
  if (h === 'admin.gowele.com')   return PORTALS.SUPER;
  if (h === 'company.gowele.com') return PORTALS.COMPANY;
  if (h === 'driver.gowele.com')  return PORTALS.DRIVER;
  if (h === 'gowele.com' || h === 'www.gowele.com') return PORTALS.PUBLIC;
  return PORTALS.DEV;
};

// Rôles RBAC autorisés par portail. `null` = portail DEV → tout autorisé.
export const rolesAllowedOnPortal = (portal) => {
  switch (portal) {
    case PORTALS.SUPER:   return ['SUPER_ADMIN'];
    case PORTALS.COMPANY: return ['ADMIN_COMPANY', ...STAFF_ROLES];
    case PORTALS.DRIVER:  return ['DRIVER'];
    case PORTALS.PUBLIC:  return ['CLIENT'];
    default:              return null; // DEV / preview
  }
};

// Normalise les rôles d'un compte backend (roles[] + type_utilisateur legacy + is_super_admin).
export const accountRoles = (user) => {
  const roles = Array.isArray(user?.roles) ? user.roles.slice() : [];
  const legacy = { super_admin: 'SUPER_ADMIN', admin: 'ADMIN_COMPANY', chauffeur: 'DRIVER', conducteur: 'DRIVER', client: 'CLIENT' }[user?.type_utilisateur];
  if (legacy && !roles.includes(legacy)) roles.push(legacy);
  if (user?.is_super_admin === true && !roles.includes('SUPER_ADMIN')) roles.push('SUPER_ADMIN');
  return roles;
};

// true si le compte a au moins un rôle autorisé sur ce portail.
export const isAccountAllowedOnPortal = (user, portal) => {
  const allowed = rolesAllowedOnPortal(portal);
  if (allowed === null) return true; // DEV → pas de blocage
  return accountRoles(user).some((r) => allowed.includes(r));
};

// Domaine « canonique » d'un portail (pour message d'erreur).
export const portalDomain = (portal) => ({
  [PORTALS.SUPER]: 'admin.gowele.com',
  [PORTALS.COMPANY]: 'company.gowele.com',
  [PORTALS.DRIVER]: 'driver.gowele.com',
  [PORTALS.PUBLIC]: 'gowele.com',
}[portal] || '');

// Domaine attendu pour un compte (où il DOIT se connecter) — 1er rôle significatif.
export const expectedDomainForAccount = (user) => {
  const roles = accountRoles(user);
  if (roles.includes('SUPER_ADMIN')) return 'admin.gowele.com';
  if (roles.includes('ADMIN_COMPANY') || roles.some((r) => STAFF_ROLES.includes(r))) return 'company.gowele.com';
  if (roles.includes('DRIVER')) return 'driver.gowele.com';
  return 'gowele.com';
};
